import { ArrowRightLeft, History, Home, Package, RefreshCw, Star, User, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import { useResidentProperty } from '@/hooks/useResidentProperty';
import { useRoleSwitcher } from '@/hooks/useRoleSwitcher';
import { useTranslation } from '@/hooks/useTranslation';
import { getPropertyConfig } from '@/services/api.client';

import { ResidentMobileNav } from '@/components/resident/ResidentMobileNav';

import { PortalLayout, type NavSection, type PortalMenuItem } from './PortalLayout';

function useNavSections(): NavSection[] {
  const { t } = useTranslation();
  return [
    {
      items: [
        { to: '/resident', label: t('resident.nav.home'), icon: Home, end: true },
        {
          to: '/resident/visitors',
          label: t('resident.nav.invitations'),
          icon: Users,
          activeMatch: ['/resident/invite', '/resident/events'],
        },
        { to: '/resident/passes', label: t('resident.nav.recurringPasses'), icon: RefreshCw },
        { to: '/resident/deliveries', label: t('resident.nav.deliveries'), icon: Package },
        { to: '/resident/favourites', label: t('resident.nav.favourites'), icon: Star },
        { to: '/resident/history', label: t('resident.nav.history'), icon: History },
        { to: '/resident/account', label: t('resident.nav.account'), icon: User },
      ],
    },
  ];
}

// Sidebar profile dropdown intentionally has no extra menu items beyond the
// role switcher. Profile, Notifications and Privacy live on the Account page.
const BASE_MENU_ITEMS: PortalMenuItem[] = [];

const ACTIVE_CLASS =
  'border-l-2 border-[var(--text-accent)] text-[var(--text-accent)] bg-[var(--bg-surface-raised)]';
const INACTIVE_CLASS = 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]';

function Brand() {
  const { t } = useTranslation();
  return (
    <div className="border-b border-[var(--bg-surface-raised)] pb-3">
      <Link to="/resident" className="flex flex-col px-2 pt-1 gap-0.5">
        <span
          className="text-lg leading-none text-[var(--text-accent)] brand-name"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          Secure Labs
        </span>
        <span className="text-[10px] text-[var(--text-tertiary)] tracking-wide group-data-[collapsible=icon]:hidden">
          {t('resident.nav.portalSubtitle')}
        </span>
      </Link>
    </div>
  );
}

function HeaderContent() {
  const { user } = useAuth();
  const prop = useResidentProperty();
  const { t } = useTranslation();
  const firstName = user?.fullName.split(' ')[0] ?? t('resident.greeting.fallbackName');

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return t('resident.greeting.morning', { name: firstName });
    if (h < 17) return t('resident.greeting.afternoon', { name: firstName });
    return t('resident.greeting.evening', { name: firstName });
  })();

  const { data: propertyConfig } = useQuery({
    queryKey: ['property-config', prop?.propertyId],
    queryFn: () => (prop ? getPropertyConfig(prop.propertyId) : null),
    enabled: !!prop,
    staleTime: 10 * 60 * 1000,
  });

  return (
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold text-[var(--text-primary)] truncate">{greeting}</p>
      {(propertyConfig?.name ?? prop?.unitId) && (
        <p className="text-xs text-[var(--text-tertiary)] truncate">
          {[
            propertyConfig?.name,
            prop?.unitName ? t('resident.greeting.unit', { name: prop.unitName }) : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      )}
    </div>
  );
}

export function ResidentLayout() {
  const { switchableRoles, switchTo, hasMultipleRoles } = useRoleSwitcher('resident');
  const { t } = useTranslation();
  const navSections = useNavSections();

  const menuItems: PortalMenuItem[] = [
    ...BASE_MENU_ITEMS,
    ...switchableRoles.map((sr) => ({
      to: sr.dashboard,
      label: t('roleSwitcher.switchTo', {
        role: t(`roleSwitcher.roles.${sr.role}`),
      }),
      icon: ArrowRightLeft,
      onClick: () => switchTo(sr),
    })),
  ];

  return (
    <PortalLayout
      brand={<Brand />}
      navSections={navSections}
      notificationsTo="/resident/notifications"
      activeClassName={ACTIVE_CLASS}
      inactiveClassName={INACTIVE_CLASS}
      headerContent={<HeaderContent />}
      menuItems={hasMultipleRoles ? menuItems : BASE_MENU_ITEMS}
      mobileNav={<ResidentMobileNav />}
    />
  );
}
